import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../Environments/Environment";
import {
  CreateShipmentRequest,
  ShipmentBoxUpsertRequest,
  ShipmentDeliverRequest,
  ShipmentDispatchRequest,
  UpdateShipmentRequest,
} from "../Dto/CreateShipmentRequest";
import { ShipmentEntity } from "../Dto/ShipmentModel";
import { ShipmentSearchRequest } from "../Dto/ShipmentSearchRequest";
import { ShipmentSearchResponse } from "../Dto/ShipmentSearchResponse";
import { sortByMaterialSequence } from "../../../Common/Utility/UtilityMaterialSequence";

@Injectable({ providedIn: "root" })
export class ShipmentApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchShipments(params: ShipmentSearchRequest): Promise<ShipmentSearchResponse> {
    const url = new URL("shipments", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();

    if (params.search) searchParams.set("search", params.search);
    if (params.warehouseId) searchParams.set("warehouseId", params.warehouseId);
    if (params.status) searchParams.set("status", params.status);
    if (params.page !== undefined) searchParams.set("page", params.page.toString());
    if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
    if (params.createdAtFrom) searchParams.set("createdAtFrom", params.createdAtFrom);
    if (params.createdAtTo) searchParams.set("createdAtTo", params.createdAtTo);

    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }

    const response = await firstValueFrom(this.http.get<ShipmentSearchResponse>(url.toString(), { withCredentials: true }));
    return {
      ...response,
      data: response.data.map((item) => this.normalizeShipment(item)),
    };
  }

  async createShipment(payload: CreateShipmentRequest): Promise<ShipmentEntity> {
    const url = new URL("shipments", `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<ShipmentEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizeShipment(response);
  }

  async getShipmentById(id: string): Promise<ShipmentEntity | null> {
    const url = new URL(`shipments/${id}`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.get<ShipmentEntity | null>(url.toString(), { withCredentials: true }));
    return (() => {
      if (response) {
        return this.normalizeShipment(response);
      }
      return null;
    })();
  }

  async updateShipment(id: string, payload: UpdateShipmentRequest): Promise<ShipmentEntity> {
    const url = new URL(`shipments/${id}`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.put<ShipmentEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizeShipment(response);
  }

  async deleteShipment(id: string): Promise<void> {
    const url = new URL(`shipments/${id}`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  async addBox(id: string, payload: ShipmentBoxUpsertRequest): Promise<ShipmentEntity> {
    const url = new URL(`shipments/${id}/boxes`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<ShipmentEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizeShipment(response);
  }

  async updateBox(id: string, boxId: string, payload: ShipmentBoxUpsertRequest): Promise<ShipmentEntity> {
    const url = new URL(`shipments/${id}/boxes/${boxId}`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.put<ShipmentEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizeShipment(response);
  }

  async deleteBox(id: string, boxId: string): Promise<ShipmentEntity> {
    const url = new URL(`shipments/${id}/boxes/${boxId}`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.delete<ShipmentEntity>(url.toString(), { withCredentials: true }));
    return this.normalizeShipment(response);
  }

  async dispatchShipment(id: string, payload: ShipmentDispatchRequest): Promise<ShipmentEntity> {
    const url = new URL(`shipments/${id}/dispatch`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<ShipmentEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizeShipment(response);
  }

  async deliverShipment(id: string, payload: ShipmentDeliverRequest): Promise<ShipmentEntity> {
    const url = new URL(`shipments/${id}/deliver`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<ShipmentEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizeShipment(response);
  }

  async downloadBoxLabelPdf(id: string, boxId: string): Promise<Blob> {
    const url = new URL(`shipments/${id}/boxes/${boxId}/label.pdf`, `${this.baseUrl}/`);
    return firstValueFrom(this.http.get(url.toString(), { responseType: "blob", withCredentials: true }));
  }

  async downloadShipmentLabelsPdf(id: string): Promise<Blob> {
    const url = new URL(`shipments/${id}/labels.pdf`, `${this.baseUrl}/`);
    return firstValueFrom(this.http.get(url.toString(), { responseType: "blob", withCredentials: true }));
  }

  private normalizeShipment(item: ShipmentEntity): ShipmentEntity {
    return {
      ...item,
      finalPartnerId: item.finalPartnerId ?? null,
      finalRecipientTeamLeaderId: item.finalRecipientTeamLeaderId ?? null,
      destinationAddress: {
        stateProvinceRegion: item.destinationAddress?.stateProvinceRegion ?? "",
        countyDistrict: item.destinationAddress?.countyDistrict ?? "",
        subdistrictMunicipality: item.destinationAddress?.subdistrictMunicipality ?? "",
        communeVillage: item.destinationAddress?.communeVillage ?? "",
        line: item.destinationAddress?.line ?? "",
        zip: item.destinationAddress?.zip ?? "",
        longitude: Number(item.destinationAddress?.longitude ?? 0),
        latitude: Number(item.destinationAddress?.latitude ?? 0),
      },
      routeType: item.routeType ?? "Direct",
      routeSummary: item.routeSummary ?? "",
      activeLeg: (() => {
        if (item.activeLeg) {
          return {
            ...item.activeLeg,
            transitPartnerId: item.activeLeg.transitPartnerId ?? null,
            transitRecipientTeamLeaderId: item.activeLeg.transitRecipientTeamLeaderId ?? null,
            destinationAddress: {
              stateProvinceRegion: item.activeLeg.destinationAddress?.stateProvinceRegion ?? "",
              countyDistrict: item.activeLeg.destinationAddress?.countyDistrict ?? "",
              subdistrictMunicipality: item.activeLeg.destinationAddress?.subdistrictMunicipality ?? "",
              communeVillage: item.activeLeg.destinationAddress?.communeVillage ?? "",
              line: item.activeLeg.destinationAddress?.line ?? "",
              zip: item.activeLeg.destinationAddress?.zip ?? "",
              longitude: Number(item.activeLeg.destinationAddress?.longitude ?? 0),
              latitude: Number(item.activeLeg.destinationAddress?.latitude ?? 0),
            },
            attachments: item.activeLeg.attachments ?? [],
          };
        }
        return null;
      })(),
      totalWeightKg: Number(item.totalWeightKg ?? 0),
      totalCubicM3: Number(item.totalCubicM3 ?? 0),
      requestCount: Number(item.requestCount ?? 0),
      boxCount: Number(item.boxCount ?? 0),
      totalRequestedQty: Number(item.totalRequestedQty ?? 0),
      totalBoxedQty: Number(item.totalBoxedQty ?? 0),
      totalDispatchedQty: Number(item.totalDispatchedQty ?? 0),
      totalDeliveredQty: Number(item.totalDeliveredQty ?? 0),
      totalRemainingQty: Number(item.totalRemainingQty ?? 0),
      canDispatch: Boolean(item.canDispatch),
      nextAction: item.nextAction ?? "",
      attachments: item.attachments ?? [],
      legs: (item.legs ?? []).map((leg) => ({
        ...leg,
        transitPartnerId: leg.transitPartnerId ?? null,
        transitRecipientTeamLeaderId: leg.transitRecipientTeamLeaderId ?? null,
        destinationAddress: {
          stateProvinceRegion: leg.destinationAddress?.stateProvinceRegion ?? "",
          countyDistrict: leg.destinationAddress?.countyDistrict ?? "",
          subdistrictMunicipality: leg.destinationAddress?.subdistrictMunicipality ?? "",
          communeVillage: leg.destinationAddress?.communeVillage ?? "",
          line: leg.destinationAddress?.line ?? "",
          zip: leg.destinationAddress?.zip ?? "",
          longitude: Number(leg.destinationAddress?.longitude ?? 0),
          latitude: Number(leg.destinationAddress?.latitude ?? 0),
        },
        attachments: leg.attachments ?? [],
      })),
      requests: (item.requests ?? []).map((request) => ({
        ...request,
        teamLeaderId: request.teamLeaderId ?? null,
        partnerId: request.partnerId ?? null,
        totalRequestedQty: Number(request.totalRequestedQty ?? 0),
        totalBoxedQty: Number(request.totalBoxedQty ?? 0),
        totalDispatchedQty: Number(request.totalDispatchedQty ?? 0),
        totalDeliveredQty: Number(request.totalDeliveredQty ?? 0),
        totalRemainingQty: Number(request.totalRemainingQty ?? 0),
        items: sortByMaterialSequence(
          (request.items ?? []).map((requestItem) => ({
            ...requestItem,
            materialSequence: Number(requestItem.materialSequence ?? 0),
            qty: Number(requestItem.qty ?? 0),
            boxedQty: Number(requestItem.boxedQty ?? 0),
            dispatchedQty: Number(requestItem.dispatchedQty ?? 0),
            deliveredQty: Number(requestItem.deliveredQty ?? 0),
          })),
          (requestItem) => ({
            sequence: requestItem.materialSequence,
            name: requestItem.materialName,
            sku: requestItem.materialSku,
          }),
        ),
      })),
      boxes: (item.boxes ?? []).map((box) => ({
        ...box,
        lengthCm: Number(box.lengthCm ?? 0),
        widthCm: Number(box.widthCm ?? 0),
        heightCm: Number(box.heightCm ?? 0),
        cubicM3: Number(box.cubicM3 ?? 0),
        weightKg: Number(box.weightKg ?? 0),
        items: sortByMaterialSequence(
          (box.items ?? []).map((boxItem) => ({
            ...boxItem,
            materialSequence: Number(boxItem.materialSequence ?? 0),
            qty: Number(boxItem.qty ?? 0),
          })),
          (boxItem) => ({
            sequence: boxItem.materialSequence,
            name: boxItem.materialName,
            sku: boxItem.materialSku,
          }),
        ),
      })),
    };
  }
}
